import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Tag, Check, X, Loader2, ShieldCheck, Pencil, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { MemoInput } from "@/components/MemoInput";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FindingTxnRow = ({ txn, findingIdx, categories, isDuplicate, siblings, onChanged }) => {
  const [mode, setMode] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveRule, setSaveRule] = useState(false);
  const [form, setForm] = useState(null);
  const fmt = (n) => `$${parseFloat(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const catLabel = categories.find(c => c.value === txn.category)?.label || txn.category;

  const run = async (fn, msg, kind = "edit") => {
    setBusy(true);
    try { await fn(); toast.success(msg); setMode(null); onChanged(kind); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const startEdit = () => { setForm({ description: txn.description || "", vendor: txn.vendor || "", amount: String(txn.amount), date: txn.date, reference: txn.reference || "" }); setMode(mode === "edit" ? null : "edit"); };
  const doEdit = () => {
    if (!form.date || !parseFloat(form.amount)) return toast.error("Amount and date required");
    run(() => axios.put(`${API}/transactions/${txn.id}`, { ...txn, ...form, amount: parseFloat(form.amount) }), "Transaction updated");
  };
  const doDelete = () => run(() => axios.delete(`${API}/transactions/${txn.id}`), "Transaction deleted");
  const doKeep = () => run(() => Promise.all(siblings.filter(s => s.id !== txn.id).map(s => axios.delete(`${API}/transactions/${s.id}`))), "Duplicates removed", "keep");
  const doRecat = async () => {
    if (!saveRule) return run(() => axios.put(`${API}/transactions/${txn.id}`, { ...txn, category: newCat }), "Category updated");
    setBusy(true);
    try {
      await axios.put(`${API}/transactions/${txn.id}`, { ...txn, category: newCat });
      const res = await axios.post(`${API}/category-rules`, { vendor: txn.vendor, category: newCat, type: txn.type, apply_existing: true });
      toast.success(`Rule saved: ${txn.vendor} is always ${categories.find(c => c.value === newCat)?.label || newCat} (${res.data.updated_count} other updated)`);
      setMode(null); onChanged("edit");
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-stone-100 rounded-lg px-3 py-1.5" data-testid={`finding-txn-${findingIdx}-${txn.id}`}>
      <div className="flex items-center gap-3 text-xs">
        <span className="text-stone-500">{txn.date}</span>
        <span className="font-medium text-stone-800">{txn.description || txn.category}</span>
        <span className="text-stone-500">{txn.vendor}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600" data-testid={`finding-txn-category-${findingIdx}-${txn.id}`}>{catLabel}</span>
        <span className="ml-auto font-medium">{fmt(txn.amount)}</span>
        {txn.locked ? (
          <span className="flex items-center gap-1 text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`finding-txn-locked-${findingIdx}-${txn.id}`}><Lock className="w-3 h-3" /> Month locked</span>
        ) : <div className="flex items-center gap-1">
          {isDuplicate && siblings.filter(x => !x.locked).length > 1 && siblings.every(x => !x.locked) && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-700 hover:bg-emerald-50" onClick={() => setMode("keep")} disabled={busy} data-testid={`keep-only-btn-${findingIdx}-${txn.id}`}>
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Keep this one
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={startEdit} disabled={busy} data-testid={`edit-txn-btn-${findingIdx}-${txn.id}`}>
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setNewCat(""); setSaveRule(false); setMode(mode === "recat" ? null : "recat"); }} disabled={busy} data-testid={`recategorize-btn-${findingIdx}-${txn.id}`}>
            <Tag className="w-3.5 h-3.5 mr-1" /> Recategorize
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => setMode("delete")} disabled={busy} data-testid={`delete-txn-btn-${findingIdx}-${txn.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>}
      </div>
      {mode === "edit" && form && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2 pb-1 items-center">
          <div className="md:col-span-2"><MemoInput className="h-8 text-xs" value={form.description} vendor={form.vendor} onChange={v => setForm(f => ({ ...f, description: v }))} testId={`edit-desc-${findingIdx}-${txn.id}`} /></div>
          <Input className="h-8 text-xs" placeholder="Provider" value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} data-testid={`edit-vendor-${findingIdx}-${txn.id}`} />
          <Input className="h-8 text-xs" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid={`edit-amount-${findingIdx}-${txn.id}`} />
          <Input className="h-8 text-xs" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid={`edit-date-${findingIdx}-${txn.id}`} />
          <div className="flex items-center gap-1">
            <Input className="h-8 text-xs" placeholder="Reference" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} data-testid={`edit-ref-${findingIdx}-${txn.id}`} />
            <Button size="sm" className="h-8 bg-stone-900 text-white" disabled={busy} onClick={doEdit} data-testid={`edit-save-${findingIdx}-${txn.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setMode(null)} data-testid={`edit-cancel-${findingIdx}-${txn.id}`}><X className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}
      {mode === "recat" && (
        <div className="flex items-center gap-2 mt-2 pb-1">
          <Select value={newCat} onValueChange={setNewCat}>
            <SelectTrigger className="h-8 w-60 text-xs" data-testid={`recategorize-select-${findingIdx}-${txn.id}`}><SelectValue placeholder="Choose new category" /></SelectTrigger>
            <SelectContent>{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="h-8 bg-stone-900 text-white" disabled={!newCat || busy} onClick={doRecat} data-testid={`recategorize-save-${findingIdx}-${txn.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setMode(null)} data-testid={`recategorize-cancel-${findingIdx}-${txn.id}`}><X className="w-3.5 h-3.5" /></Button>
          {txn.vendor && (
            <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer ml-2">
              <Checkbox checked={saveRule} onCheckedChange={(v) => setSaveRule(!!v)} data-testid={`save-rule-checkbox-${findingIdx}-${txn.id}`} />
              Always file <span className="font-medium text-stone-800">{txn.vendor}</span> as this category
            </label>
          )}
        </div>
      )}
      <AlertDialog open={mode === "delete" || mode === "keep"} onOpenChange={(o) => !o && setMode(null)}>
        <AlertDialogContent data-testid={`finding-confirm-dialog-${findingIdx}-${txn.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{mode === "keep" ? "Keep this transaction?" : "Delete transaction?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {mode === "keep"
                ? `The other ${siblings.length - 1} transaction(s) in this finding will be permanently deleted.`
                : `"${txn.description || catLabel}" for ${fmt(txn.amount)} on ${txn.date} will be permanently deleted.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finding-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={mode === "keep" ? doKeep : doDelete} data-testid="finding-confirm-ok">
              {mode === "keep" ? "Delete others" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
